/**
 * analytics — first-party beacon dispatch helper.
 *
 * Feb 2026 Analytics Commit A. Wire per design doc §6.1.
 *
 * Client-side surface:
 *   * `trackEvent(name, extraProps?)` — the primary entry point.
 *     Fires a fire-and-forget POST to `/api/beacon/event` using
 *     `navigator.sendBeacon` when available (>99% of modern
 *     browsers). Falls back to `fetch(..., { keepalive: true })`
 *     when sendBeacon is missing. Never blocks the caller,
 *     never throws, never toasts on failure.
 *   * `shouldSkipAnalytics({ user? })` — exclusion check that
 *     mirrors the server-side `/admin/*` + impersonation rules.
 *     Callers use this to short-circuit BEFORE calling
 *     `trackEvent` — cheap client-side gate that avoids sending
 *     a beacon we know the server would drop.
 *
 * The event-name whitelist in `backend/analytics_beacon.py`
 * (`ALLOWED_EVENT_NAMES`) is the source of truth. Frontend passes
 * strings; unknown names are silently dropped server-side.
 */

const BEACON_PATH = "/api/beacon/event";

function beaconUrl() {
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) return null;
  return `${base}${BEACON_PATH}`;
}

/**
 * Extract UTM + referrer context from the current window. Called
 * per beacon dispatch so URL param changes on SPA navigation are
 * picked up.
 */
function currentPageContext() {
  if (typeof window === "undefined") return {};
  const loc = window.location;
  const params = new URLSearchParams(loc.search || "");
  return {
    path: loc.pathname || "/",
    referrer: (typeof document !== "undefined" && document.referrer) || null,
    utm_source: params.get("utm_source") || null,
    utm_medium: params.get("utm_medium") || null,
    utm_campaign: params.get("utm_campaign") || null,
  };
}

/**
 * Client-side exclusion mirror of the server's beacon gates.
 * Returns true when the beacon MUST NOT fire from the current
 * session. Matches the server contract in
 * `backend/analytics_beacon.py::beacon_event`:
 *
 *   * `/admin/*` routes — analytics doesn't measure ops surfaces.
 *   * Impersonation session — support engineers inside customer
 *     accounts must never emit route history to analytics.
 *
 * Admin-role check happens SERVER-SIDE only (JWT resolution).
 * We could shortcut here if we wanted, but keeping the client
 * check minimal reduces the client-server contract surface.
 */
export function shouldSkipAnalytics({ pathname, isImpersonating } = {}) {
  if (isImpersonating) return true;
  if (typeof pathname === "string" && pathname.startsWith("/admin")) {
    return true;
  }
  return false;
}

/**
 * Fire the beacon. Never blocks, never throws.
 *
 * Payload matches `BeaconEventBody` in the backend module.
 * Server derives `visitor_hash`, `session_hash`, `ts`,
 * `event_id`, `user_id`, `referrer_domain` — client MUST NOT
 * send those fields.
 */
export function trackEvent(eventName, extraProps = {}) {
  if (typeof eventName !== "string" || !eventName) return;
  const url = beaconUrl();
  if (!url) return;

  const ctx = currentPageContext();
  const body = JSON.stringify({
    event_name: eventName,
    path: ctx.path,
    referrer: ctx.referrer,
    utm_source: ctx.utm_source,
    utm_medium: ctx.utm_medium,
    utm_campaign: ctx.utm_campaign,
    provider_id: extraProps.provider_id || null,
    props: extraProps.props || {},
  });

  try {
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function"
    ) {
      // sendBeacon takes a Blob or string; sending as Blob keeps
      // the Content-Type header pinnable so the server routes it
      // correctly. FastAPI's Pydantic body parser accepts
      // application/json.
      const blob = new Blob([body], { type: "application/json" });
      const queued = navigator.sendBeacon(url, blob);
      if (queued) return;
      // Fall through to fetch on rare sendBeacon rejection
      // (e.g. queue full, quota exceeded).
    }
    if (typeof fetch === "function") {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {
        // Analytics MUST NEVER surface an error to the caller.
      });
    }
  } catch {
    // Defensive — never let a broken beacon crash the app.
  }
}

/**
 * Convenience wrapper for the `page.view` event (Commit A's
 * shipped event). Called by `AnalyticsPageView` on route change.
 */
export function trackPageView(opts = {}) {
  if (shouldSkipAnalytics(opts)) return;
  trackEvent("page.view");
}

export const _internals = { beaconUrl, currentPageContext };
